import { IconTile } from "@peer-start/ui";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import type { ActionButtonProps, ActionLinkProps } from "../types/navigation.js";

const actionClassName = (compact: boolean, className: string) =>
  `group flex w-full items-center gap-3 rounded-xl border border-border/70 bg-background text-left shadow-xs transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30 ${compact ? "min-h-16 px-3 py-2.5" : "min-h-20 p-4 sm:gap-4"} ${className}`;

function ActionBody({
  title,
  description,
  icon,
  iconTone,
  badge,
  children,
}: Pick<ActionLinkProps, "title" | "description" | "icon" | "iconTone" | "badge" | "children">) {
  return (
    <>
      {icon ? <IconTile tone={iconTone}>{icon}</IconTile> : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span className="text-sm font-medium leading-snug text-foreground">{title}</span>
          {badge}
        </div>
        {description ? (
          <span className="mt-1 block text-xs leading-relaxed text-secondary-text">{description}</span>
        ) : null}
        {children}
      </div>
      <ChevronRight
        aria-hidden="true"
        className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
      />
    </>
  );
}

export function ActionLink({
  controlId,
  title,
  description,
  icon,
  iconTone,
  compact = false,
  badge,
  children,
  className = "",
  ...props
}: ActionLinkProps) {
  return (
    <Link className={actionClassName(compact, className)} data-control-id={controlId} {...props}>
      <ActionBody badge={badge} description={description} icon={icon} iconTone={iconTone} title={title}>
        {children}
      </ActionBody>
    </Link>
  );
}

export function ActionButton({
  controlId,
  title,
  description,
  icon,
  iconTone,
  compact = false,
  badge,
  children,
  className = "",
  onClick,
}: ActionButtonProps) {
  return (
    <button
      className={actionClassName(compact, className)}
      data-control-id={controlId}
      onClick={onClick}
      type="button"
    >
      <ActionBody badge={badge} description={description} icon={icon} iconTone={iconTone} title={title}>
        {children}
      </ActionBody>
    </button>
  );
}
